<template>
  <div class="flex w-80 flex-shrink-0 flex-col border-r border-slate-200 bg-white">
    <div class="border-b border-slate-100 p-4">
      <CompactFilterBar
        shell-class="mb-0 rounded-xl border border-slate-200 bg-slate-50/70"
        body-class="p-3"
      >
        <div class="filter-bar-field-sm relative">
          <svg class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input
            v-model="chatStore.searchKeyword"
            type="text"
            placeholder="搜索联系人或消息..."
            class="form-search-input form-control-sm w-full"
          >
        </div>

        <template #actions>
          <div class="flex w-full items-center justify-between gap-3">
            <h2 class="text-lg font-semibold text-slate-800">消息</h2>
            <button
              class="cursor-pointer rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-primary-50 hover:text-primary-500"
              title="新建会话"
              @click="chatStore.showNewConversationDialog = true"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
            </button>
          </div>
        </template>
      </CompactFilterBar>
    </div>

    <div class="flex items-center gap-1 border-b border-slate-100 p-2 px-4">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        class="cursor-pointer rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
        :class="chatStore.filterTab === tab.value
          ? 'bg-primary-50 text-primary-700'
          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'"
        @click="chatStore.filterTab = tab.value"
      >
        {{ tab.label }}
        <span
          v-if="tab.count > 0"
          class="ml-1 rounded-full bg-red-500 px-1.5 py-0.5 text-xs leading-none text-white"
        >
          {{ tab.count > 99 ? '99+' : tab.count }}
        </span>
      </button>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div v-if="chatStore.loading" class="space-y-3 p-4">
        <div v-for="i in 5" :key="i" class="flex animate-pulse items-center gap-3">
          <div class="h-10 w-10 flex-shrink-0 rounded-full bg-slate-200"></div>
          <div class="flex-1">
            <div class="mb-2 h-4 w-24 rounded bg-slate-200"></div>
            <div class="h-3 w-36 rounded bg-slate-100"></div>
          </div>
        </div>
      </div>

      <div v-else-if="chatStore.filteredConversations.length === 0" class="p-8 text-center">
        <svg class="mx-auto mb-2 h-12 w-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
        </svg>
        <p class="text-sm text-slate-400">暂无会话</p>
      </div>

      <div v-else>
        <div
          v-for="conv in chatStore.filteredConversations"
          :key="conv.id"
          class="group/conv relative flex cursor-pointer items-center gap-3 border-l-2 px-4 py-3 transition-colors"
          :class="[
            chatStore.currentConversationId === conv.id
              ? 'border-l-primary-500 bg-primary-50/70'
              : 'border-l-transparent hover:bg-slate-50',
            chatStore.isConversationPinned(conv.id) ? 'bg-amber-50/40' : ''
          ]"
          @click="chatStore.selectConversation(conv.id)"
          @contextmenu.prevent="openConvMenu($event, conv)"
        >
          <div class="relative flex-shrink-0">
            <img
              v-if="conv.type !== 'group' && getConvAvatar(conv)"
              :src="getConvAvatar(conv)"
              class="h-10 w-10 rounded-full object-cover"
            />
            <div
              v-else-if="conv.type !== 'group'"
              class="flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium text-white"
              :class="conv.type === 'internal' ? 'bg-primary-500' : 'bg-emerald-500'"
            >
              {{ conv.name.slice(-2) }}
            </div>
            <div
              v-else
              class="flex h-10 w-10 items-center justify-center rounded-full bg-violet-500"
            >
              <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
            <span
              v-if="conv.type !== 'group' && conv.online"
              class="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-green-400"
            ></span>
          </div>

          <div class="min-w-0 flex-1">
            <div class="mb-0.5 flex items-center justify-between">
              <div class="flex min-w-0 items-center gap-1.5">
                <span class="truncate text-sm font-medium text-slate-800">{{ conv.name }}</span>
                <svg
                  v-if="chatStore.isConversationPinned(conv.id)"
                  class="h-3 w-3 flex-shrink-0 text-amber-500"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                </svg>
                <svg
                  v-if="chatStore.isConversationMuted(conv.id)"
                  class="h-3 w-3 flex-shrink-0 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clip-rule="evenodd"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"/>
                </svg>
                <span
                  v-if="conv.type === 'internal'"
                  class="flex-shrink-0 rounded bg-primary-100 px-1 py-0.5 text-[10px] leading-none text-primary-600"
                >内部</span>
                <span
                  v-else-if="conv.type === 'customer'"
                  class="flex-shrink-0 rounded bg-emerald-100 px-1 py-0.5 text-[10px] leading-none text-emerald-600"
                >客户</span>
              </div>
              <span class="ml-2 flex-shrink-0 text-[11px] text-slate-400">{{ formatTime(conv.lastMessageTime) }}</span>
            </div>
            <div class="flex items-center justify-between">
              <p class="truncate text-xs text-slate-500">{{ conv.lastMessage }}</p>
              <span
                v-if="conv.unreadCount > 0"
                class="ml-2 flex h-[18px] min-w-[18px] flex-shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-medium text-white"
                :class="chatStore.isConversationMuted(conv.id) ? 'bg-slate-400' : 'bg-red-500'"
              >
                {{ conv.unreadCount > 99 ? '99+' : conv.unreadCount }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showConvMenu"
        class="fixed inset-0 z-[100]"
        @click="showConvMenu = false"
        @contextmenu.prevent="showConvMenu = false"
      >
        <div
          class="absolute z-[101] min-w-[140px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          :style="{ left: convMenuPos.x + 'px', top: convMenuPos.y + 'px' }"
        >
          <button
            class="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm text-slate-600 hover:bg-slate-50"
            @click="handlePin"
          >
            <svg class="h-4 w-4 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
            </svg>
            {{ chatStore.isConversationPinned(activeConv?.id) ? '取消置顶' : '置顶' }}
          </button>
          <button
            class="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm text-slate-600 hover:bg-slate-50"
            @click="handleMute"
          >
            <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/>
            </svg>
            {{ chatStore.isConversationMuted(activeConv?.id) ? '取消免打扰' : '消息免打扰' }}
          </button>
          <div class="my-1 border-t border-slate-100"></div>
          <button
            class="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm text-red-500 hover:bg-red-50"
            @click="handleDeleteConv"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
            删除会话
          </button>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import CompactFilterBar from '@/components/CompactFilterBar.vue'
import { useChatStore } from '@/stores/chat'
import { useUserStore } from '@/stores/user'

const chatStore = useChatStore()
const userStore = useUserStore()
const currentUserId = computed(() => userStore.userInfo?.userId)

function getConvAvatar(conv) {
  if (conv.type === 'group') return ''
  const other = (conv.members || []).find(m => String(m.id) !== String(currentUserId.value))
  if (other) return chatStore.getUserInfo(other.id).avatar
  return ''
}

const showConvMenu = ref(false)
const convMenuPos = ref({ x: 0, y: 0 })
const activeConv = ref(null)

function openConvMenu(e, conv) {
  activeConv.value = conv
  convMenuPos.value = { x: e.clientX, y: e.clientY }
  showConvMenu.value = true
}

function handlePin() {
  if (activeConv.value) {
    chatStore.pinConversation(activeConv.value.id)
    ElMessage.success(chatStore.isConversationPinned(activeConv.value.id) ? '已置顶' : '已取消置顶')
  }
  showConvMenu.value = false
}

function handleMute() {
  if (activeConv.value) {
    chatStore.muteConversation(activeConv.value.id)
    ElMessage.success(chatStore.isConversationMuted(activeConv.value.id) ? '已开启免打扰' : '已关闭免打扰')
  }
  showConvMenu.value = false
}

function handleDeleteConv() {
  if (activeConv.value) {
    chatStore.deleteConversation(activeConv.value.id)
    ElMessage.success('会话已删除')
  }
  showConvMenu.value = false
}

const tabs = computed(() => {
  const countByType = (type) =>
    chatStore.conversations.filter(c => c.type === type).reduce((sum, c) => sum + (c.unreadCount || 0), 0)

  return [
    { value: 'all', label: '全部', count: chatStore.totalUnreadCount },
    { value: 'internal', label: '内部', count: countByType('internal') },
    { value: 'customer', label: '客户', count: countByType('customer') },
    { value: 'group', label: '群聊', count: countByType('group') }
  ]
})

function formatTime(timeStr) {
  if (!timeStr) return ''
  const date = new Date(timeStr)
  if (Number.isNaN(date.getTime())) return ''
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  if (isToday) return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) return '昨天'
  return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })
}
</script>
